// pages/CallLogs/DailerRecoring/RecordingIndex.jsx

import React, { useState, useEffect, useRef } from "react";
import "./RecordingIndex.css";
import MainLayout from "../../../layouts/MainLayout";
import { Recording_URL, Auth_KEY } from "../../Config";
import CronJobsPanel from "./CronJobsPanel";

const API_BASE = `${Recording_URL}api`;
const API_KEY  = `${Auth_KEY}`;

const headers = {
  "Content-Type": "application/json",
  "X-API-Key":    API_KEY,
};

// ── Helpers ───────────────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("en-PK", {
    day:    "2-digit",
    month:  "short",
    year:   "numeric",
    hour:   "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// ── Shared: load servers from API ─────────────────────────────────────────
async function fetchServersFromAPI() {
  const res  = await fetch(
    `${Recording_URL}api/recording-servers/active?page=1&limit=100`,
    { headers }
  );
  const data = await res.json();
  if (!data.success) throw new Error("Could not load server list.");
  return data.data || [];
}

// ── Shared: Server Dropdown ───────────────────────────────────────────────
function ServerSelect({ value, onChange, servers, loading, placeholder = "Select Server" }) {
  return (
    <select
      className="form-control form-select"
      value={value}
      onChange={onChange}
      disabled={loading}
    >
      <option value="">
        {loading ? "Loading servers..." : placeholder}
      </option>
      {servers.map(s => (
        <option key={s.id} value={s.server_key}>
          {s.folder} ({s.server_key})
        </option>
      ))}
    </select>
  );
}

function Alert({ type, message, onClose }) {
  if (!message) return null;
  const styles = {
    success: "alert alert-success",
    danger:  "alert alert-danger",
    warning: "alert alert-warning",
    info:    "alert alert-info",
  };
  return (
    <div
      className={`${styles[type] || "alert alert-info"} alert-dismissible fade show d-flex align-items-center gap-2`}
      role="alert"
    >
      <i
        className={`bi ${
          type === "success"
            ? "bi-check-circle-fill"
            : type === "danger"
            ? "bi-x-circle-fill"
            : type === "warning"
            ? "bi-exclamation-triangle-fill"
            : "bi-info-circle-fill"
        }`}
      ></i>
      <span>{message}</span>
      <button type="button" className="btn-close ms-auto" onClick={onClose}></button>
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    completed: { cls: "badge-status-completed", icon: "bi-check-circle-fill", label: "Completed" },
    running:   { cls: "badge-status-running",   icon: "bi-arrow-repeat",       label: "Running"   },
    pending:   { cls: "badge-status-pending",   icon: "bi-hourglass-split",    label: "Pending"   },
    failed:    { cls: "badge-status-failed",    icon: "bi-x-circle-fill",      label: "Failed"    },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`badge-status ${s.cls}`}>
      <i className={`bi ${s.icon} me-1 ${status === "running" ? "spin-icon" : ""}`}></i>
      {s.label}
    </span>
  );
}

// ── Progress Bar ──────────────────────────────────────────────────────────
function ProgressBar({ total, inserted, skipped }) {
  if (!total || total === 0) return <span className="text-muted small">—</span>;
  const pct = Math.round(((inserted + skipped) / total) * 100);
  return (
    <div>
      <div className="d-flex justify-content-between mb-1">
        <small className="text-muted">{inserted + skipped} / {total}</small>
        <small className="fw-semibold">{pct}%</small>
      </div>
      <div className="progress" style={{ height: "6px" }}>
        <div
          className="progress-bar bg-success"
          style={{ width: `${Math.round((inserted / total) * 100)}%` }}
        ></div>
        <div
          className="progress-bar bg-warning"
          style={{ width: `${Math.round((skipped / total) * 100)}%` }}
        ></div>
      </div>
      <div className="d-flex gap-3 mt-1">
        <small className="text-success"><i className="bi bi-check2 me-1"></i>{inserted} inserted</small>
        <small className="text-warning"><i className="bi bi-skip-forward me-1"></i>{skipped} skipped</small>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TAB 1 — Start Indexing
// ─────────────────────────────────────────────────────────────────────────
function StartIndexing({ onJobStarted, servers, serversLoading }) {
  const [indexType, setIndexType] = useState("single");
  const [server,    setServer]    = useState("");
  const [callDate,  setCallDate]  = useState("");
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");
  const [loading,   setLoading]   = useState(false);
  const [alert,     setAlert]     = useState(null);

  const today = new Date().toISOString().split("T")[0];

  async function handleSubmit(e) {
    e.preventDefault();

    if (!server.trim()) {
      setAlert({ type: "warning", message: "Server is required." });
      return;
    }
    if (indexType === "single" && !callDate) {
      setAlert({ type: "warning", message: "Call date is required for single day indexing." });
      return;
    }
    if (indexType === "range" && (!dateFrom || !dateTo)) {
      setAlert({ type: "warning", message: "Both From and To dates are required for range indexing." });
      return;
    }
    if (indexType === "range" && dateFrom > dateTo) {
      setAlert({ type: "warning", message: "From date cannot be after To date." });
      return;
    }

    setLoading(true);
    setAlert(null);

    try {
      let url = `${API_BASE}/recordings/index?server=${encodeURIComponent(server.trim())}`;
      if (indexType === "single") {
        url += `&call_date=${callDate}`;
      } else {
        url += `&call_dateFrom=${dateFrom}&call_dateTo=${dateTo}`;
      }

      const res  = await fetch(url, { headers });
      const data = await res.json();

      if (!data.success) throw new Error(data.message || "API error");

      setAlert({
        type:    "success",
        message: `✅ Indexing started! Job ID: ${data.job_id}`,
      });
      onJobStarted && onJobStarted(data.job_id);
    } catch (err) {
      setAlert({ type: "danger", message: err.message });
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setServer("");
    setCallDate("");
    setDateFrom("");
    setDateTo("");
    setAlert(null);
  }

  return (
    <div>
      <Alert {...(alert || {})} onClose={() => setAlert(null)} />

      <form onSubmit={handleSubmit}>
        <div className="row g-3">

          {/* Index Type */}
          <div className="col-12">
            <label className="form-label fw-semibold">
              Index Type <span className="text-danger">*</span>
            </label>
            <div className="d-flex gap-4 mt-1">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  id="it_single"
                  value="single"
                  checked={indexType === "single"}
                  onChange={() => setIndexType("single")}
                />
                <label className="form-check-label" htmlFor="it_single">
                  <i className="bi bi-calendar-day me-1 text-primary"></i>Single Day
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  id="it_range"
                  value="range"
                  checked={indexType === "range"}
                  onChange={() => setIndexType("range")}
                />
                <label className="form-check-label" htmlFor="it_range">
                  <i className="bi bi-calendar-range me-1 text-info"></i>Date Range
                </label>
              </div>
            </div>
          </div>

          {/* Server Dropdown — shared from parent */}
          <div className="col-md-6">
            <label className="form-label fw-semibold">
              Server <span className="text-danger">*</span>
            </label>
            <ServerSelect
              value={server}
              onChange={e => setServer(e.target.value)}
              servers={servers}
              loading={serversLoading}
            />
            {serversLoading && (
              <div className="mt-1">
                <small className="text-muted">
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Loading servers...
                </small>
              </div>
            )}
          </div>

          {/* Single Day */}
          {indexType === "single" && (
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Call Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={callDate}
                onChange={e => setCallDate(e.target.value)}
                max={today}
              />
            </div>
          )}

          {/* Date Range */}
          {indexType === "range" && (
            <>
              <div className="col-md-3">
                <label className="form-label fw-semibold">
                  From Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  max={today}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label fw-semibold">
                  To Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  max={today}
                  min={dateFrom || undefined}
                />
              </div>
            </>
          )}

          {/* Info Box */}
          <div className="col-12">
            <div className="info-box">
              <i className="bi bi-info-circle-fill text-primary me-2"></i>
              <span>
                {indexType === "single"
                  ? "Single day indexing will process all recordings for the selected date on the specified server."
                  : "Date range indexing processes recordings across multiple days. Large ranges may take longer to complete."}
              </span>
            </div>
          </div>

          {/* Submit */}
          <div className="col-12 d-flex gap-2">
            <button type="submit" className="btn btn-primary px-4" disabled={loading || serversLoading}>
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>Starting...</>
              ) : (
                <><i className="bi bi-play-circle me-2"></i>Start Indexing</>
              )}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={handleReset}>
              <i className="bi bi-arrow-counterclockwise me-1"></i>Reset
            </button>
          </div>

        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TAB 2 — Check Status
// ─────────────────────────────────────────────────────────────────────────
function CheckStatus({ prefillJobId }) {
  const [jobId,   setJobId]   = useState(prefillJobId || "");
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [alert,   setAlert]   = useState(null);
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (prefillJobId) {
      setJobId(prefillJobId);
      setResult(null);
      fetchStatus(prefillJobId, true);
    }
    // eslint-disable-next-line
  }, [prefillJobId]);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  async function fetchStatus(id, autoPoll = false) {
    const jid = (id || jobId).trim();
    if (!jid) {
      setAlert({ type: "warning", message: "Job ID is required." });
      return;
    }
    setLoading(true);
    setAlert(null);

    try {
      const res  = await fetch(
        `${API_BASE}/recordings/index/status?job_id=${encodeURIComponent(jid)}`,
        { headers }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "API error");
      setResult(data);

      if (autoPoll && data.status === "running") {
        startPolling(jid);
      } else if (data.status !== "running") {
        stopPolling();
      }
    } catch (err) {
      setAlert({ type: "danger", message: err.message });
      stopPolling();
    } finally {
      setLoading(false);
    }
  }

  function startPolling(jid) {
    if (pollRef.current) return;
    setPolling(true);
    pollRef.current = setInterval(() => {
      fetchStatus(jid, false).then(() => {
        setResult(prev => {
          if (prev && prev.status !== "running") stopPolling();
          return prev;
        });
      });
    }, 5000);
  }

  function stopPolling() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }

  function handleCheck(e) {
    e.preventDefault();
    stopPolling();
    fetchStatus(jobId, true);
  }

  return (
    <div>
      <Alert {...(alert || {})} onClose={() => setAlert(null)} />

      <form onSubmit={handleCheck} className="row g-3 mb-4">
        <div className="col-md-8">
          <label className="form-label fw-semibold">Job ID</label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <i className="bi bi-hash text-primary"></i>
            </span>
            <input
              type="text"
              className="form-control form-control-lg font-mono"
              placeholder="e.g. job_1781448846849_7a1ksoc"
              value={jobId}
              onChange={e => setJobId(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-4 d-flex align-items-end gap-2">
          <button className="btn btn-info btn-lg text-white w-100" disabled={loading}>
            {loading ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Fetching...</>
            ) : (
              <><i className="bi bi-arrow-clockwise me-2"></i>Check Status</>
            )}
          </button>
        </div>
      </form>

      {polling && (
        <div className="polling-banner mb-3">
          <span className="spinner-border spinner-border-sm me-2 text-primary"></span>
          Auto-refreshing every 5 seconds...
          <button className="btn btn-sm btn-outline-secondary ms-3" onClick={stopPolling}>
            Stop
          </button>
        </div>
      )}

      {result && (
        <div className="status-card">
          <div className="status-card-header">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="status-icon">
                <i className="bi bi-diagram-3"></i>
              </div>
              <div className="flex-grow-1">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h6 className="mb-0 fw-bold">Job Details</h6>
                  <StatusBadge status={result.status} />
                </div>
                <code className="text-muted small">{result.job_id}</code>
              </div>
            </div>
          </div>

          <div className="status-grid">
            <div className="status-item">
              <span className="status-label"><i className="bi bi-server me-1"></i>Server</span>
              <span className="status-value">{result.server || "—"}</span>
            </div>
            <div className="status-item">
              <span className="status-label"><i className="bi bi-activity me-1"></i>Action</span>
              <span className="status-value">{result.action || "—"}</span>
            </div>
            <div className="status-item">
              <span className="status-label"><i className="bi bi-calendar me-1"></i>Date Range</span>
              <span className="status-value">
                {result.range?.call_date
                  ? result.range.call_date
                  : result.range?.date_from
                  ? `${result.range.date_from} → ${result.range.date_to}`
                  : "—"}
              </span>
            </div>
            <div className="status-item">
              <span className="status-label"><i className="bi bi-clock-history me-1"></i>Duration</span>
              <span className="status-value">
                {result.progress?.duration_ms != null
                  ? `${result.progress.duration_ms} ms`
                  : "—"}
              </span>
            </div>
            <div className="status-item">
              <span className="status-label"><i className="bi bi-play-circle me-1"></i>Created</span>
              <span className="status-value">{formatDate(result.created_at)}</span>
            </div>
            <div className="status-item">
              <span className="status-label"><i className="bi bi-arrow-repeat me-1"></i>Updated</span>
              <span className="status-value">{formatDate(result.updated_at)}</span>
            </div>
          </div>

          <div className="status-progress-section">
            <h6 className="fw-semibold mb-3">
              <i className="bi bi-bar-chart-fill me-2 text-primary"></i>Progress
            </h6>
            <ProgressBar
              total={result.progress?.total_records ?? result.result?.total ?? 0}
              inserted={result.progress?.inserted ?? result.result?.inserted ?? 0}
              skipped={result.progress?.skipped ?? result.result?.skipped ?? 0}
            />
          </div>

          <div className="stats-row mt-3">
            <div className="stat-mini stat-total">
              <span className="stat-mini-num">{result.progress?.total_records ?? result.result?.total ?? 0}</span>
              <span className="stat-mini-lbl">Total</span>
            </div>
            <div className="stat-mini stat-inserted">
              <span className="stat-mini-num">{result.progress?.inserted ?? result.result?.inserted ?? 0}</span>
              <span className="stat-mini-lbl">Inserted</span>
            </div>
            <div className="stat-mini stat-skipped">
              <span className="stat-mini-num">{result.progress?.skipped ?? result.result?.skipped ?? 0}</span>
              <span className="stat-mini-lbl">Skipped</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TAB 3 — Indexing History
// ─────────────────────────────────────────────────────────────────────────
function IndexingHistory({ servers, serversLoading }) {
  const [jobs,       setJobs]       = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // filters
  const [server,   setServer]   = useState("");
  const [status,   setStatus]   = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line
  }, [page]);

  async function loadJobs() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, action: "indexRecordings" });
      if (status)   params.append("status",    status);
      if (server)   params.append("server",    server);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo)   params.append("date_to",   dateTo);

      const res  = await fetch(`${API_BASE}/jobs?${params.toString()}`, { headers });
      const data = await res.json();

      if (data.success) {
        setJobs(data.data || []);
        setTotalPages(data.pagination?.totalPages || 1);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function handleSearch() {
    setPage(1);
    loadJobs();
  }

  function handleReset() {
    setServer("");
    setStatus("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  }

  const totalRecords  = jobs.reduce((s, j) => s + (j.total_records || 0), 0);
  const totalInserted = jobs.reduce((s, j) => s + (j.inserted      || 0), 0);
  const totalSkipped  = jobs.reduce((s, j) => s + (j.skipped       || 0), 0);

  return (
    <div>

      {/* SUMMARY CARDS */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card shadow-sm border-0 bg-primary text-white">
            <div className="card-body">
              <h3>{jobs.length}</h3>
              <small>Total Jobs</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm border-0 bg-success text-white">
            <div className="card-body">
              <h3>{totalRecords.toLocaleString()}</h3>
              <small>Total Records</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm border-0 bg-info text-white">
            <div className="card-body">
              <h3>{totalInserted.toLocaleString()}</h3>
              <small>Inserted</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card shadow-sm border-0 bg-warning text-dark">
            <div className="card-body">
              <h3>{totalSkipped.toLocaleString()}</h3>
              <small>Skipped</small>
            </div>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">

            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="completed">Completed</option>
                <option value="running">Running</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {/* ── Server Dropdown (same API source as StartIndexing) ── */}
            <div className="col-md-3">
              <label className="form-label">Server</label>
              <ServerSelect
                value={server}
                onChange={e => setServer(e.target.value)}
                servers={servers}
                loading={serversLoading}
                placeholder="All Servers"
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">From</label>
              <input
                type="date"
                className="form-control"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">To</label>
              <input
                type="date"
                className="form-control"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                min={dateFrom || undefined}
              />
            </div>

            <div className="col-md-3 d-flex align-items-end gap-2">
              <button className="btn btn-primary" onClick={handleSearch}>
                <i className="bi bi-search me-2"></i>Search
              </button>
              <button className="btn btn-outline-secondary" onClick={handleReset}>
                Reset
              </button>
              <button className="btn btn-outline-info" onClick={loadJobs} title="Refresh">
                <i className="bi bi-arrow-clockwise"></i>
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* TABLE */}
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-bold">Recording Index Jobs</h6>
          <span className="badge bg-light text-dark">{jobs.length} Records</span>
        </div>

        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-5 text-center text-muted">No jobs found</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Status</th>
                    <th>Server</th>
                    <th>Date Range</th>
                    <th>Total</th>
                    <th>Inserted</th>
                    <th>Skipped</th>
                    <th>Duration</th>
                    <th>Created</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map(job => {
                    let summary = null;
                    try {
                      summary = job.result_summary ? JSON.parse(job.result_summary) : null;
                    } catch { summary = null; }

                    return (
                      <tr key={job.id}>
                        <td><StatusBadge status={job.status} /></td>
                        <td><span className="fw-semibold">{job.server}</span></td>
                        <td>{job.call_date || `${job.date_from} → ${job.date_to}`}</td>
                        <td>{job.total_records?.toLocaleString()}</td>
                        <td className="text-success fw-semibold">{job.inserted?.toLocaleString()}</td>
                        <td className="text-warning fw-semibold">{job.skipped?.toLocaleString()}</td>
                        <td>{job.duration_ms ? `${(job.duration_ms / 1000).toFixed(1)}s` : "-"}</td>
                        <td>{formatDate(job.created_at)}</td>
                        <td>
                          {summary && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              data-bs-toggle="collapse"
                              data-bs-target={`#summary-${job.id}`}
                            >
                              View
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {jobs.map(job => {
                let summary = null;
                try { summary = job.result_summary ? JSON.parse(job.result_summary) : null; } catch {}
                return (
                  <div
                    key={`collapse-${job.id}`}
                    className="collapse border-top p-3 bg-light"
                    id={`summary-${job.id}`}
                  >
                    <pre className="mb-0">{JSON.stringify(summary, null, 2)}</pre>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* PAGINATION */}
        <div className="card-footer bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted">Page {page} of {totalPages}</span>
            <div className="btn-group">
              <button
                className="btn btn-outline-secondary"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </button>
              <button
                className="btn btn-outline-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MAIN PAGE — servers loaded ONCE here, passed as props to all tabs
// ─────────────────────────────────────────────────────────────────────────
export default function RecordingIndex() {
  const [activeTab,      setActiveTab]      = useState("start");
  const [latestJobId,    setLatestJobId]    = useState(null);
  const [servers,        setServers]        = useState([]);
  const [serversLoading, setServersLoading] = useState(false);
  const [serversAlert,   setServersAlert]   = useState(null);

  // Load servers once at top level — shared across all tabs
  useEffect(() => {
    (async () => {
      setServersLoading(true);
      try {
        const list = await fetchServersFromAPI();
        setServers(list);
      } catch (err) {
        setServersAlert({ type: "warning", message: err.message });
      } finally {
        setServersLoading(false);
      }
    })();
  }, []);

  function handleJobStarted(jobId) {
    setLatestJobId(jobId);
    setTimeout(() => setActiveTab("status"), 800);
  }

  return (
    <MainLayout>
      <div className="recording-index-page">

        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-4 page-header">
          <div className="d-flex align-items-center gap-3">
            <div className="header-icon">
              <i className="bi bi-database-gear"></i>
            </div>
            <div>
              <h4 className="mb-0 fw-bold">Recording Indexing</h4>
              <p className="text-muted mb-0 small">
                Index call recordings by date or date range for a specific server
              </p>
            </div>
          </div>
        </div>

        {/* Global server load error */}
        {serversAlert && (
          <Alert {...serversAlert} onClose={() => setServersAlert(null)} />
        )}

        <div className="card shadow-sm border-0">
          <div className="card-header bg-white border-bottom px-4 pt-3 pb-0">
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link fw-semibold ${activeTab === "start" ? "active" : ""}`}
                  onClick={() => setActiveTab("start")}
                >
                  <i className="bi bi-play-circle me-2"></i>Start Indexing
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link fw-semibold ${activeTab === "status" ? "active text-info" : ""}`}
                  onClick={() => setActiveTab("status")}
                >
                  <i className="bi bi-activity me-2"></i>Check Status
                  {latestJobId && activeTab !== "status" && (
                    <span className="badge bg-primary ms-2" style={{ fontSize: "10px" }}>New</span>
                  )}
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link fw-semibold ${activeTab === "history" ? "active" : ""}`}
                  onClick={() => setActiveTab("history")}
                >
                  <i className="bi bi-clock-history me-2"></i>Indexing History
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link fw-semibold ${activeTab === "cron" ? "active" : ""}`}
                  onClick={() => setActiveTab("cron")}
                >
                  <i className="bi bi-alarm me-2"></i>Cron Jobs
                </button>
              </li>
            </ul>
          </div>

          <div className="card-body p-4">
            {activeTab === "start" && (
              <StartIndexing
                onJobStarted={handleJobStarted}
                servers={servers}
                serversLoading={serversLoading}
              />
            )}
            {activeTab === "status" && (
              <CheckStatus prefillJobId={latestJobId} />
            )}
            {activeTab === "history" && (
              <IndexingHistory
                servers={servers}
                serversLoading={serversLoading}
              />
            )}
            {activeTab === "cron" && <CronJobsPanel />}
          </div>
        </div>

      </div>
    </MainLayout>
  );
}